"use client";

import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "../../layouts/Header";
import { useAuth } from "../../context/AuthContext";
import { setNotifChange } from "../../services/settingsService";
import { useToast } from "../../hooks/use-toast";
import Toaster from "../../components/ui/toaster";

const SettingsNotifications = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { settings, updateSettings } = useAuth();
  const [account, setAccount] = useState(
    settings?.notifications?.accountActivity ?? true
  );
  const [price, setPrice] = useState(
    settings?.notifications?.priceAlerts ?? true
  );
  const [product, setProduct] = useState(
    settings?.notifications?.productAnnouncements ?? true
  );
  const [loading, setLoading] = useState(() => new Map());

  const isLoading = (key) => loading.has(key) && loading.get(key);

  const updateLoading = (key, value) => {
    setLoading((prev) => new Map(prev).set(key, value));
  };

  const handleNotifChange = async (apiType, key, setValue, previousValue) => {
    updateLoading(key, true);
    try {
      const apiCall = await setNotifChange({
        type: apiType,
        state: !previousValue,
      });
      if (apiCall.status === 200 && apiCall.data.status === true) {
        updateSettings(apiCall.data.data);
        toast({
          description: "Notification preference updated",
          variant: "eighty",
        });
      } else {
        setValue(previousValue);
        toast({
          description: "Failed to update notification preference",
          variant: "destructive",
        });
      }
    } catch (err) {
      setValue(previousValue);
      toast({
        description: "Failed to update notification preference",
        variant: "destructive",
      });
    } finally {
      updateLoading(key, false);
    }
  };

  const toggleAccount = (e) => {
    if (isLoading("acc")) return;
    const previousValue = account;
    setAccount(e);
    handleNotifChange("account-activity", "acc", setAccount, previousValue);
  };

  const togglePrice = (e) => {
    if (isLoading("price")) return;
    const previousValue = price;
    setPrice(e);
    handleNotifChange("price-alerts", "price", setPrice, previousValue);
  };

  // Product announcements toggle - disabled until feature is ready
  // const toggleProduct = (e) => {
  //   if (isLoading("product")) return;
  //   const previousValue = product;
  //   setProduct(e);
  //   handleNotifChange(
  //     "product-announcements",
  //     "product",
  //     setProduct,
  //     previousValue
  //   );
  // };

  useEffect(() => {
    if (settings?.notifications) {
      setAccount(settings.notifications.accountActivity ?? true);
      setPrice(settings.notifications.priceAlerts ?? true);
      setProduct(settings.notifications.productAnnouncements ?? true);
    }
  }, [settings]);

  return (
    <>
      <Header />
      <div className="min-h-screen w-full bg-gray-100 flex flex-col">
        {/* Top Gradient Bar */}
        <div
          className="h-14 bg-gradient-to-r from-blue-600 to-purple-600 flex items-center pl-20 text-white"
          onClick={() => {
            navigate("/settings/index");
          }}
        >
          <ArrowLeft className="mr-5 bg-transparent rounded-full p-1 border-gray-200/60 border hover:border-gray-400 hover:bg-white/20" />
          <p className="text-sm font-medium">Settings / Notifications</p>
        </div>

        {/* Main Content */}
        <div className="flex justify-center mt-20 px-4 md:px-16 lg:px-32 bg-gray-100">
          <div className="max-w-3xl w-full">
            <Card className="bg-white rounded-2xl shadow-sm">
              <CardHeader className="px-6 pt-6 pb-4">
                <CardTitle className="text-lg font-semibold text-gray-500">
                  Notifications
                </CardTitle>
              </CardHeader>

              <CardContent className="px-6 pb-6">
                {/* Notifications List */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between px-4 py-6 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
                    <div>
                      <p className="text-md font-medium text-gray-700">
                        Account activity
                      </p>
                      <p className="text-xs text-gray-500">
                        All app transactions
                      </p>
                    </div>
                    <div className="flex space-x-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={account}
                          onCheckedChange={toggleAccount}
                          disabled={isLoading("acc")}
                          className={`data-[state=checked]:bg-accent-pink data-[state=checked]:border-transparent rounded-sm w-8 h-8 border-[2px] border-bg-border ${isLoading("acc") ? "opacity-50 cursor-not-allowed" : ""}`}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-7 bg-white border border-gray-300 rounded-sm hover:bg-gray-50">
                    <div>
                      <p className="text-md font-medium text-gray-700">
                        Price Alerts
                      </p>
                    </div>
                    <div className="flex space-x-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={price}
                          onCheckedChange={togglePrice}
                          disabled={isLoading("price")}
                          className={`data-[state=checked]:bg-accent-pink data-[state=checked]:border-transparent rounded-sm w-8 h-8 border-[2px] border-bg-border ${isLoading("price") ? "opacity-50 cursor-not-allowed" : ""}`}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between px-4 py-6 bg-white border border-gray-300 rounded-sm opacity-60">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-md font-medium text-gray-700">
                          Product announcements
                        </p>
                        <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-700 rounded-full">
                          Coming Soon
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Learn about our newest features
                      </p>
                    </div>
                    <div className="flex space-x-4">
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={product}
                          disabled={true}
                          className="data-[state=checked]:bg-accent-pink data-[state=checked]:border-transparent rounded-sm w-8 h-8 border-[2px] border-bg-border opacity-50 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Toaster className="bg-accent-blue" />
    </>
  );
};

export default SettingsNotifications;
